import api from './api'
import { safeBrandingPath } from '@/utils/brandingPath'
import { toCamelCase } from '@/utils/transform'

/**
 * Who this deployment serves. Client identity is configuration, never a literal
 * in a component — the server resolves it from stored settings, then the
 * environment, then a neutral fallback.
 *
 * This is also where multi-tenancy will attach: when a deployment serves more
 * than one client, this resolves per tenant and every consumer follows without
 * changing.
 */
export interface ClientProfile {
  name: string
  shortName: string
  /** The lender's legal entity as a contract names it; '' until recorded. */
  legalName: string
  /** For dark backgrounds (the app shell, login). */
  logoLight: string
  /** For light backgrounds and print. */
  logoDark: string
  /** Square-ish mark for report headers. */
  logoMark: string
  primaryColor: string
  /** The legal entity a term sheet is issued by ("… (Lender)", the LENDER signature). */
  lenderLegalName: string
  /** The lender's brand line ("… reserves the right to modify …"). */
  lenderName: string
  /** Term-sheet letterhead image, served from /branding. */
  letterheadLogo: string
}

/** The profile with every logo reduced to a same-origin /branding path (or none). */
function withSafeLogos(profile: ClientProfile): ClientProfile {
  return {
    ...profile,
    logoLight: safeBrandingPath(profile.logoLight),
    logoDark: safeBrandingPath(profile.logoDark),
    logoMark: safeBrandingPath(profile.logoMark),
    lenderLegalName: profile.lenderLegalName ?? '',
    lenderName: profile.lenderName ?? '',
    letterheadLogo: safeBrandingPath(profile.letterheadLogo ?? ''),
  }
}

export const clientProfileService = {
  get: () =>
    api.get('/settings/client-profile').then((r) =>
      withSafeLogos(toCamelCase<{ data: ClientProfile }>(r.data).data),
    ),

  update: (patch: Partial<ClientProfile>) =>
    api.patch('/settings/client-profile', patch).then((r) =>
      withSafeLogos(toCamelCase<{ data: ClientProfile }>(r.data).data),
    ),
}
